'use client'

import { AlertTriangleIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import {
  ACCOUNT_CONNECTION_EMPTY_CLASS,
  ACCOUNT_CONNECTION_SHEET_CLASS,
} from '@/features/dashboard/components/account-connection/styles'
import { paradigmErrorMessage, useParadigmBinding } from '@/hooks/use-paradigm-binding'
import {
  paradigmBindingStateKey,
  type ParadigmBindingTone,
  paradigmBindingTone,
  paradigmEvidenceKey,
  paradigmManualLinkName,
  sortParadigmAccounts,
} from '@/lib/paradigm/binding'
import { formatRelativeTime } from '@/lib/relative-time'
import { cn } from '@/lib/utils'
import type { ParadigmBindingState } from '@/types/generated/common/account'
import type { ParadigmAccountInfo, ParadigmConnectDeskResponse } from '@/types/generated/public_api'

// Binds ONE Deribit or Bybit OrderX account to a nickname on a Paradigm desk.
//
// Three server steps, in order: register the desk's credentials, list the nicknames it
// labels its own trading accounts with, bind one. A desk registered earlier skips
// straight to the picker (`skipCredentials`) — re-linking a different nickname is the
// only "manage" action there is, since the backend exposes no unlink and
// `paradigmLinkAccount` is an unconditional overwrite.
//
// Every refusal here is server-side and verbatim: unsupported venue, missing Admin
// access, `paradigm` not on the plan. Nothing is pre-judged locally.
type ParadigmDeskSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: string
  /** Desk already registered (a binding exists) — open on the picker, not the key form. */
  skipCredentials: boolean
  /** Nickname to preselect, e.g. a MISMATCH's `suggestedAccountName`. */
  highlightAccountName?: string
  onComplete: () => void | Promise<void>
}

type Step = 'credentials' | 'picker' | 'done'

/** Badge palette for a binding state, shared with the account row's own badge. */
export const PARADIGM_TONE_BADGE: Record<ParadigmBindingTone, string> = {
  verified: 'border-chart-mint/30 bg-chart-mint/10 text-chart-mint',
  unverified: 'border-ox-gold/30 bg-ox-gold/10 text-ox-gold/90',
  mismatch: 'border-chart-red/30 bg-chart-red/10 text-chart-red',
}

// Desk-shaped field names. Paired with autoComplete="new-password" these keep Chrome and
// the password managers from treating the two inputs as a site login and prefilling them.
const ACCESS_KEY_FIELD = 'paradigm-desk-access-key'
const SECRET_KEY_FIELD = 'paradigm-desk-secret'
const MANUAL_NAME_FIELD = 'paradigm-desk-account-name'

const TONE_DETAIL: Record<ParadigmBindingTone, string> = {
  verified: 'paradigmStateVerifiedDetail',
  unverified: 'paradigmStateUnverifiedDetail',
  mismatch: 'paradigmStateMismatchDetail',
}

export function ParadigmDeskSheet({
  open,
  onOpenChange,
  accountId,
  skipCredentials,
  highlightAccountName,
  onComplete,
}: ParadigmDeskSheetProps) {
  const t = useTranslations('accountConnection')
  const tCommon = useTranslations('common')
  const { connectDesk, listAccounts, linkAccount } = useParadigmBinding()
  const [step, setStep] = useState<Step>(skipCredentials ? 'picker' : 'credentials')
  const [accessKey, setAccessKey] = useState('')
  const [secretKey, setSecretKey] = useState('')
  const [desk, setDesk] = useState<ParadigmConnectDeskResponse | null>(null)
  const [accounts, setAccounts] = useState<ParadigmAccountInfo[] | null>(null)
  const [linkedName, setLinkedName] = useState('')
  const [linkedState, setLinkedState] = useState<ParadigmBindingState | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [listing, setListing] = useState(false)
  const [linking, setLinking] = useState('')
  // Free-text fallback for a nickname `list_accounts` cannot return (see the input's own
  // comment). Preserved verbatim apart from surrounding whitespace — the binding is an
  // exact match against the desk's nickname.
  const [manualName, setManualName] = useState('')
  const [error, setError] = useState('')
  // Bumped by Retry so the listing effect re-runs without needing the failed attempt's
  // state to be distinguishable from "never fetched".
  const [listAttempt, setListAttempt] = useState(0)

  // Read only when the sheet opens. Linking the FIRST nickname makes a binding appear
  // upstream, flipping `skipCredentials` false → true while the sheet is still open —
  // depending on it directly would fire the reset below and yank the user off the result
  // step they were just shown.
  const skipCredentialsRef = useRef(skipCredentials)
  useEffect(() => {
    skipCredentialsRef.current = skipCredentials
  })

  useEffect(() => {
    if (!open) return
    // A fresh sheet every open: credentials are never retained across one, and the
    // candidate list is refetched so its trade counts reflect what the desk has done
    // since.
    setStep(skipCredentialsRef.current ? 'picker' : 'credentials')
    setAccessKey('')
    setSecretKey('')
    setDesk(null)
    setAccounts(null)
    setLinkedName('')
    setLinkedState(null)
    setManualName('')
    setError('')
  }, [open])

  useEffect(() => {
    if (!open || step !== 'picker') return
    let cancelled = false
    const run = async () => {
      setListing(true)
      setError('')
      try {
        const result = await listAccounts(accountId)
        if (cancelled) return
        setAccounts(sortParadigmAccounts(result.accounts))
      } catch (e) {
        if (cancelled) return
        setError(paradigmErrorMessage(e, t('paradigmListFailed')))
      } finally {
        if (!cancelled) setListing(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [open, step, listAttempt, accountId, listAccounts, t])

  const handleConnect = async () => {
    if (!accessKey.trim() || !secretKey.trim() || connecting) return
    setConnecting(true)
    setError('')
    try {
      const result = await connectDesk(accountId, accessKey.trim(), secretKey.trim())
      setDesk(result)
      // Drop the secret from component state the moment the server has it.
      setAccessKey('')
      setSecretKey('')
      setStep('picker')
    } catch (e) {
      setError(paradigmErrorMessage(e, t('paradigmConnectFailed')))
    } finally {
      setConnecting(false)
    }
  }

  const handleLink = async (accountName: string) => {
    if (linking) return
    setLinking(accountName)
    setError('')
    try {
      const result = await linkAccount(accountId, accountName)
      setLinkedName(accountName)
      setLinkedState(result.state)
      setStep('done')
      await onComplete()
    } catch (e) {
      setError(paradigmErrorMessage(e, t('paradigmLinkFailed')))
    } finally {
      setLinking('')
    }
  }

  const trimmedManualName = paradigmManualLinkName(manualName)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className={ACCOUNT_CONNECTION_SHEET_CLASS}>
        <SheetHeader>
          <SheetTitle>
            {step === 'done'
              ? t('paradigmLinkedTitle')
              : skipCredentials
                ? t('paradigmChangeAccountTitle')
                : t('paradigmConnectTitle')}
          </SheetTitle>
        </SheetHeader>

        {error && <p className="text-chart-red text-sm">{error}</p>}

        {step === 'credentials' && (
          <div className="flex flex-col gap-4 text-sm">
            <p className="text-foreground/70">{t('paradigmConnectIntro')}</p>
            {/* A text input immediately followed by a type="password" one is what every
                browser and password manager heuristically reads as a login form, and
                `autoComplete="off"` is widely ignored. `new-password` IS honoured, and
                the ids/names are deliberately desk-shaped — anything containing user,
                email, login or a bare "password" re-arms the same heuristic. There is no
                <form> around these, which keeps the heuristic weaker; don't add one. The
                secret stays type="password": masking beats autofill avoidance. */}
            <label className="flex flex-col gap-1.5" htmlFor={ACCESS_KEY_FIELD}>
              <span className="text-foreground/60">{t('paradigmAccessKey')}</span>
              <Input
                type="text"
                id={ACCESS_KEY_FIELD}
                name={ACCESS_KEY_FIELD}
                value={accessKey}
                onChange={(e) => setAccessKey(e.target.value)}
                placeholder={t('paradigmAccessKeyPlaceholder')}
                autoComplete="new-password"
                data-1p-ignore={true}
                data-lpignore="true"
                inputClassName="h-9 border-0 bg-white/5 placeholder:text-white/30"
                disabled={connecting}
              />
            </label>
            <label className="flex flex-col gap-1.5" htmlFor={SECRET_KEY_FIELD}>
              <span className="text-foreground/60">{t('paradigmSecretKey')}</span>
              <Input
                type="password"
                id={SECRET_KEY_FIELD}
                name={SECRET_KEY_FIELD}
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder={t('paradigmSecretKeyPlaceholder')}
                autoComplete="new-password"
                data-1p-ignore={true}
                data-lpignore="true"
                inputClassName="h-9 border-0 bg-white/5 placeholder:text-white/30"
                disabled={connecting}
              />
            </label>
            <Button
              variant="basic"
              type="button"
              className="w-fit gap-1.5"
              disabled={!accessKey.trim() || !secretKey.trim() || connecting}
              onClick={() => void handleConnect()}
            >
              {connecting && <Loader2Icon className="size-3 animate-spin" />}
              {connecting ? t('paradigmConnecting') : t('paradigmConnectButton')}
            </Button>
          </div>
        )}

        {step === 'picker' && (
          <div className="flex flex-col gap-4 text-sm">
            {/* Only after a connect in THIS session — a previously registered desk's id
                isn't carried on the account listing, so there's nothing to show for it. */}
            {desk && (
              <dl className="flex flex-col gap-2 border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground/60">{t('paradigmDeskId')}</dt>
                  <dd className="truncate font-mono text-xs">{desk.deskId}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground/60">{t('paradigmDeskName')}</dt>
                  <dd className="text-foreground/80 truncate text-xs">
                    {desk.deskName || t('paradigmDeskNamePending')}
                  </dd>
                </div>
              </dl>
            )}
            <p className="text-foreground/70">{t('paradigmPickerIntro')}</p>
            {listing ? (
              <div className="text-foreground/60 flex items-center justify-center gap-2 py-8">
                <Loader2Icon className="size-4 animate-spin" />
                <span>{t('paradigmLoadingAccounts')}</span>
              </div>
            ) : accounts && accounts.length > 0 ? (
              <div className="flex flex-col gap-2">
                {accounts.map((info) => (
                  <div
                    key={info.accountName}
                    className={cn(
                      'flex flex-col gap-2 border border-white/10 bg-white/5 p-3',
                      info.accountName === highlightAccountName && 'border-chart-mint/40'
                    )}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {info.accountName}
                      </span>
                      <span className="text-foreground/50 shrink-0 bg-white/10 px-1.5 py-0.5 text-[10px] font-medium tracking-tight uppercase">
                        {t(paradigmEvidenceKey(info.evidence))}
                      </span>
                    </div>
                    <div className="text-foreground/50 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      <span>
                        {t('paradigmTradeCounts', {
                          trades: info.paradigmTradeCount,
                          matched: info.matchedTradeCount,
                        })}
                      </span>
                      {info.lastTradedAtMs && (
                        <span>
                          {t('paradigmLastTraded', {
                            when: formatRelativeTime(info.lastTradedAtMs, tCommon),
                          })}
                        </span>
                      )}
                    </div>
                    <Button
                      variant="basic"
                      type="button"
                      className="w-fit gap-1.5"
                      disabled={!!linking}
                      onClick={() => void handleLink(info.accountName)}
                    >
                      {linking === info.accountName && (
                        <Loader2Icon className="size-3 animate-spin" />
                      )}
                      {linking === info.accountName
                        ? t('paradigmLinking')
                        : t('paradigmLinkButton')}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              // Distinguishes "the desk labels no accounts" from a failed listing — the
              // error above already covers the latter, and retry has to stay reachable.
              <div className="flex flex-col gap-3">
                <p className={ACCOUNT_CONNECTION_EMPTY_CLASS}>
                  {accounts ? t('paradigmPickerEmpty') : t('paradigmListUnavailable')}
                </p>
                <Button
                  variant="basic"
                  type="button"
                  className="w-fit"
                  onClick={() => setListAttempt((n) => n + 1)}
                >
                  {t('paradigmRetryList')}
                </Button>
              </div>
            )}

            {/* Rendered outside the list/empty branches above, so it is reachable in
                every settled state — most of all the empty one, which is exactly a desk
                whose accounts have never traded. `list_accounts` only returns nicknames
                Paradigm has seen TRADES under, so a brand-new subaccount is invisible to
                it; `link_account` binds an unlisted name anyway. Still shown when the
                listing FAILED: a user who knows their own nickname should not be blocked
                by a down RPC. */}
            {!listing && (
              <div className="flex flex-col gap-2 border border-white/10 bg-white/5 p-3">
                <label className="text-foreground/70" htmlFor={MANUAL_NAME_FIELD}>
                  {t('paradigmManualTitle')}
                </label>
                <Input
                  type="text"
                  id={MANUAL_NAME_FIELD}
                  name={MANUAL_NAME_FIELD}
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder={t('paradigmManualPlaceholder')}
                  autoComplete="off"
                  data-1p-ignore={true}
                  data-lpignore="true"
                  inputClassName="h-9 border-0 bg-white/5 placeholder:text-white/30"
                  disabled={!!linking}
                />
                <p className="text-foreground/50 text-xs">{t('paradigmManualExact')}</p>
                {/* The honest half: `networks_for` gates Paradigm fan-out on a VERIFIED
                    binding, so a name linked this way is bound but not yet routed. */}
                <p className="text-ox-gold/80 text-xs">{t('paradigmManualUnverified')}</p>
                <Button
                  variant="basic"
                  type="button"
                  className="w-fit gap-1.5"
                  disabled={!trimmedManualName || !!linking}
                  onClick={() => {
                    if (trimmedManualName) void handleLink(trimmedManualName)
                  }}
                >
                  {linking === trimmedManualName && <Loader2Icon className="size-3 animate-spin" />}
                  {linking === trimmedManualName ? t('paradigmLinking') : t('paradigmManualLink')}
                </Button>
              </div>
            )}
          </div>
        )}

        {step === 'done' && linkedState !== null && (
          <div className="flex flex-col gap-4 text-sm">
            <div className="flex items-center gap-2">
              {paradigmBindingTone(linkedState) === 'verified' ? (
                <CheckCircle2Icon className="text-chart-mint size-4 shrink-0" />
              ) : (
                <AlertTriangleIcon className="text-ox-gold/70 size-4 shrink-0" />
              )}
              <span className="min-w-0 flex-1 truncate font-medium">{linkedName}</span>
              <span
                className={cn(
                  'shrink-0 border px-2 py-0.5 text-[10px] font-medium',
                  PARADIGM_TONE_BADGE[paradigmBindingTone(linkedState)]
                )}
              >
                {t(paradigmBindingStateKey(linkedState))}
              </span>
            </div>
            <p className="text-foreground/60 text-xs">
              {t(TONE_DETAIL[paradigmBindingTone(linkedState)])}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="basic" type="button" onClick={() => onOpenChange(false)}>
                {t('paradigmDone')}
              </Button>
              {/* Linking is an unconditional overwrite, so a wrong pick is corrected by
                  picking again — never by an unlink, which the backend doesn't expose. */}
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  setStep('picker')
                  setListAttempt((n) => n + 1)
                }}
              >
                {t('paradigmPickAgain')}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
